import { pluggableAccountFunctions } from "./pluggableAccountFunctions";
import { logGreyErrorFromAccounts } from "owa-account-analytics";
import type { ApplicationCloudEnvironment } from "../types/ApplicationCloudEnvironment";

/**
 * Session cache of the resolved application environment. The environment does not change within a
 * session, so once the pluggable getApplicationEnvironment source has been invoked we reuse its
 * promise. A failed resolution is not cached (see below); the error is logged and rethrown, so a
 * later call can retry.
 */
let cachedApplicationEnvironment: Promise<ApplicationCloudEnvironment | undefined> | undefined;

/**
 * Gets the application environment as reported by the pluggable function, which currently interops with the native host getApplicationEnvironment API.
 *
 * IMPORTANT: resolves to undefined when the default application environment was used (i.e.
 * application launched from the Public cloud) or when the pluggable getter is not yet initialized.
 * A failure from the underlying native-host call is logged and rethrown (not mapped to undefined),
 * so consumers that treat a missing environment as "default cloud" aren't misled by a transient
 * failure.
 */
export function getApplicationEnvironment(): Promise<ApplicationCloudEnvironment | undefined> {
    if (cachedApplicationEnvironment === undefined) {
        const pluggableGetApplicationEnvironment = pluggableAccountFunctions?.getApplicationEnvironment;
        if (!pluggableGetApplicationEnvironment) {
            // Not initialized yet — log and don't cache, so a later call (once wired up) can
            // resolve it.
            logGreyErrorFromAccounts("GetApplicationEnvironment_NotInitialized", pluggableAccountFunctions?.tryGetGlobalSettingsAccountMailboxInfo?.());
            return Promise.resolve(undefined);
        }
        
        cachedApplicationEnvironment = pluggableGetApplicationEnvironment().catch((error: unknown) => {
            // Don't cache failures — clear the memo so a later call can retry
            cachedApplicationEnvironment = undefined;
            logGreyErrorFromAccounts("GetApplicationEnvironment_Error", pluggableAccountFunctions?.tryGetGlobalSettingsAccountMailboxInfo?.(), error instanceof Error ? error : undefined);
            throw error;
        });
    }
    
    return cachedApplicationEnvironment;
}

/** @internal Test-only. */
export function resetApplicationEnvironmentCacheForTests(): void {
    cachedApplicationEnvironment = undefined;
}
